/**
 * Demo Mode Redaction Utilities
 * 
 * When demo mode is enabled, sensitive information is redacted:
 * - Vehicle IDs: "Vehicle 1", "Vehicle 2", etc.
 * - Vehicle Types: "Type A", "Type B", etc.
 * - Station Names: "Station A", "Station B", etc.
 */

// Maps to store original -> redacted mappings
const vehicleIdMap = new Map<string, string>();
const vehicleTypeMap = new Map<string, string>();
const stationNameMap = new Map<string, string>();
const countyMap = new Map<string, string>();
const callTypeMap = new Map<string, string>();
const transportServiceMap = new Map<string, string>();
const cityNameMap = new Map<string, string>();

// Counters for generating sequential IDs
let vehicleIdCounter = 1;
let vehicleTypeCounter = 1;
let stationNameCounter = 1;
let countyCounter = 1;
let callTypeCounter = 1;
let transportServiceCounter = 1;
let cityNameCounter = 1;

/**
 * Reset all redaction mappings (useful when demo mode is toggled)
 */
export function resetRedactionMaps() {
  vehicleIdMap.clear();
  vehicleTypeMap.clear();
  stationNameMap.clear();
  countyMap.clear();
  callTypeMap.clear();
  transportServiceMap.clear();
  cityNameMap.clear();
  vehicleIdCounter = 1;
  vehicleTypeCounter = 1;
  stationNameCounter = 1;
  countyCounter = 1;
  callTypeCounter = 1;
  transportServiceCounter = 1;
  cityNameCounter = 1;
}

/**
 * Redact a vehicle ID
 */
export function redactVehicleId(originalId: string, isDemoMode: boolean): string {
  if (!isDemoMode || !originalId) return originalId;
  
  if (!vehicleIdMap.has(originalId)) {
    vehicleIdMap.set(originalId, `Vehicle ${vehicleIdCounter}`);
    vehicleIdCounter++;
  }
  
  return vehicleIdMap.get(originalId) || originalId;
}

/**
 * Redact a vehicle type
 */
export function redactVehicleType(originalType: string, isDemoMode: boolean): string {
  if (!isDemoMode || !originalType) return originalType;
  
  if (!vehicleTypeMap.has(originalType)) {
    // Convert counter to letter (A, B, C, ...)
    const letter = String.fromCharCode(64 + vehicleTypeCounter); // 65 = 'A'
    vehicleTypeMap.set(originalType, `Type ${letter}`);
    vehicleTypeCounter++;
  }
  
  return vehicleTypeMap.get(originalType) || originalType;
}

/**
 * Redact a station name
 */
export function redactStationName(originalName: string, isDemoMode: boolean): string {
  if (!isDemoMode || !originalName) return originalName;
  
  if (!stationNameMap.has(originalName)) {
    // Convert counter to letter (A, B, C, ...)
    const letter = String.fromCharCode(64 + stationNameCounter); // 65 = 'A'
    stationNameMap.set(originalName, `Station ${letter}`);
    stationNameCounter++;
  }
  
  return stationNameMap.get(originalName) || originalName;
}

/**
 * Redact a county name
 */
export function redactCounty(originalCounty: string, isDemoMode: boolean): string {
  if (!isDemoMode || !originalCounty) return originalCounty;
  
  if (!countyMap.has(originalCounty)) {
    // Convert counter to letter (A, B, C, ...)
    const letter = String.fromCharCode(64 + countyCounter); // 65 = 'A'
    countyMap.set(originalCounty, `County ${letter}`);
    countyCounter++;
  }
  
  return countyMap.get(originalCounty) || originalCounty;
}

/**
 * Redact a call type/category name
 */
export function redactCallType(originalCallType: string, isDemoMode: boolean): string {
  if (!isDemoMode || !originalCallType) return originalCallType;
  
  if (!callTypeMap.has(originalCallType)) {
    // Convert counter to letter (A, B, C, ...)
    const letter = String.fromCharCode(64 + callTypeCounter); // 65 = 'A'
    callTypeMap.set(originalCallType, `Call Type ${letter}`);
    callTypeCounter++;
  }
  
  return callTypeMap.get(originalCallType) || originalCallType;
}

/**
 * Redact a transport service name
 */
export function redactTransportService(originalService: string, isDemoMode: boolean): string {
  if (!isDemoMode || !originalService) return originalService;
  
  if (!transportServiceMap.has(originalService)) {
    // Convert counter to letter (A, B, C, ...)
    const letter = String.fromCharCode(64 + transportServiceCounter); // 65 = 'A'
    transportServiceMap.set(originalService, `Service ${letter}`);
    transportServiceCounter++;
  }
  
  return transportServiceMap.get(originalService) || originalService;
}

/**
 * Redact a city name
 */
export function redactCityName(originalCity: string, isDemoMode: boolean): string {
  if (!isDemoMode || !originalCity) return originalCity;
  
  if (!cityNameMap.has(originalCity)) {
    // Convert counter to letter (A, B, C, ...)
    const letter = String.fromCharCode(64 + cityNameCounter); // 65 = 'A'
    cityNameMap.set(originalCity, `City ${letter}`);
    cityNameCounter++;
  }
  
  return cityNameMap.get(originalCity) || originalCity;
}

/**
 * Redact an object with common fields
 */
export function redactObject<T extends Record<string, unknown>>(
  obj: T,
  isDemoMode: boolean
): T {
  if (!isDemoMode || !obj) return obj;

  // Indexed by known field names below; T is generic so TS can't verify
  // those fields exist on it statically — widen to a plain record for the
  // mutation, then hand back the same object shape via the return type.
  const redacted: Record<string, unknown> = { ...obj };

  // Redact common field names. The redactors expect string values; unknown
  // fields are asserted at the call sites to preserve the original runtime
  // behaviour (truthy non-string values still fall through unchanged).
  if (redacted.vehicle_id) {
    redacted.vehicle_id = redactVehicleId(redacted.vehicle_id as string, isDemoMode);
  }
  if (redacted.vehicleId) {
    redacted.vehicleId = redactVehicleId(redacted.vehicleId as string, isDemoMode);
  }
  if (redacted['Vehicle ID']) {
    redacted['Vehicle ID'] = redactVehicleId(redacted['Vehicle ID'] as string, isDemoMode);
  }
  
  if (redacted.vehicle_type) {
    redacted.vehicle_type = redactVehicleType(redacted.vehicle_type as string, isDemoMode);
  }
  if (redacted.vehicleType) {
    redacted.vehicleType = redactVehicleType(redacted.vehicleType as string, isDemoMode);
  }
  if (redacted['Vehicle Type']) {
    redacted['Vehicle Type'] = redactVehicleType(redacted['Vehicle Type'] as string, isDemoMode);
  }
  if (redacted['VEHICLE TYPE']) {
    redacted['VEHICLE TYPE'] = redactVehicleType(redacted['VEHICLE TYPE'] as string, isDemoMode);
  }
  
  if (redacted.fire_station) {
    redacted.fire_station = redactStationName(redacted.fire_station as string, isDemoMode);
  }
  if (redacted.fireStation) {
    redacted.fireStation = redactStationName(redacted.fireStation as string, isDemoMode);
  }
  if (redacted.station) {
    redacted.station = redactStationName(redacted.station as string, isDemoMode);
  }
  if (redacted['Fire Station']) {
    redacted['Fire Station'] = redactStationName(redacted['Fire Station'] as string, isDemoMode);
  }
  if (redacted['Station']) {
    redacted['Station'] = redactStationName(redacted['Station'] as string, isDemoMode);
  }
  
  return redacted as T;
}

/**
 * Redact an array of objects
 */
export function redactArray<T extends Record<string, unknown>>(
  arr: T[],
  isDemoMode: boolean
): T[] {
  if (!isDemoMode || !arr) return arr;
  return arr.map(obj => redactObject(obj, isDemoMode));
}

